<template>
  <session-content-container :title="$t('animation.menu.games.criticalChain')">
    <template v-if="game">
      <v-row>
        <v-col
          v-for="player in game.playerList"
          :key="`player-${player.id}`"
          cols="12"
          md="6"
          lg="4"
          xl="3"
        >
          <player-card
            :value="player"
            :selected="currentPlayerId === player.id"
            @click.native="currentPlayerId = player.id"
          />
        </v-col>
      </v-row>
      <v-row v-if="currentPlayerId">
        <v-col>
          <v-card>
            <v-toolbar
              color="secondary"
              dark
              dense
              flat
            >
              <v-spacer />
              <v-toolbar-title
                v-text="ganttCardTitle"
              />
              <v-spacer />
            </v-toolbar>

            <v-card-text>
              <frappe-gantt
                :key="`gantt-${currentPlayer.id}`"
                :value="currentPlayerTask"
                show-conflict
                select-key="isOnCriticalChain"
                fixed
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-alert
        v-else
        prominent
        text
        icon="mdi-information-outline"
        color="info"
        class="mx-10 mt-5 pa-5"
      >
        <div
          class="headline text-center"
          v-text="$t('animation.criticalchain.unselectedCardMessage')"
        />
      </v-alert>
    </template>
  </session-content-container>
</template>

<script>
import { mapActions } from 'vuex'
import _throttle from 'lodash/throttle'
import Repositories from 'src/repositories'

import SessionContentContainer from '../SessionContentContainer'
import PlayerCard from '../../components/critical-chain/PlayerCard'
import FrappeGantt from '../../components/critical-chain/FrappeGantt'

export default {
  name: 'CriticalChainContainer',
  components: {
    SessionContentContainer,
    PlayerCard,
    FrappeGantt,
  },
  props: {
    sessionId: {
      type: String,
      required: true,
    },
    gameId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    game: null,
    currentPlayerId: null,
  }),
  computed: {
    currentPlayer () {
      return this.currentPlayerId && this.game.playerList
        .find(player => player.id === this.currentPlayerId)
    },
    currentPlayerTask () {
      if (this.currentPlayer) {
        const bufferLabel = this.$t('animation.criticalchain.buffer', {
          size: this.currentPlayer.bufferSize || this.$t('animation.criticalchain.bufferNotDefined'),
        })
        return this.currentPlayer.taskList.map(item => (item.id === 'Buffer'
          ? { ...item, name: bufferLabel }
          : item))
      }
      return []
    },
    ganttCardTitle () {
      if (this.currentPlayer?.playerName) {
        return this.$t('animation.criticalchain.selectedPlayer', {
          playerName: this.currentPlayer.playerName,
        })
      }
      return this.$t('animation.criticalchain.notPlayerName')
    },
  },
  sockets: {
    trainingSessionPlayerLoggedIn ({ idSession }) {
      if (this.sessionId === idSession) {
        this.fetchData()
      }
    },
    criticalChainSessionPlayerCanRemoveMultiTask ({ criticalChainSessionId }) {
      if (this.gameId === criticalChainSessionId) {
        this.fetchData()
      }
    },
    criticalChainSessionPlayerCanSelectCriticalChain ({ criticalChainSessionId }) {
      if (this.gameId === criticalChainSessionId) {
        this.fetchData()
      }
    },
    criticalChainSessionPlayerCanDefineBufferSize ({ criticalChainSessionId }) {
      if (this.gameId === criticalChainSessionId) {
        this.fetchData()
      }
    },
    criticalChainSessionPlayerFinished ({ criticalChainSessionId }) {
      if (this.gameId === criticalChainSessionId) {
        this.fetchData()
      }
    },
  },
  watch: {
    $route: {
      immediate: true,
      async handler () {
        this.fetchData()
      },
    },
  },
  methods: {
    ...mapActions('ui', {
      showErrorNotification: 'showErrorNotification',
    }),
    fetchData: _throttle(async function fetchData () {
      this.game = await Repositories.animation.criticalchain.get(this.gameId)
      // Show message only during the first load (not on data refresh)
      this.loading = false
    }, 200, { leading: true, trailing: true }),
  },
}
</script>
